import * as amplitude from "@amplitude/unified";
import { logAmplitudeEvent } from "@risalabs_frontend_org/logger";
import { AmplitudeEventPayload, BaseEventInput } from "../types/amplitudeEvent";
import { buildBaseEvent } from "./buildBaseEvent";

/**
 * `additional_data` keys that duplicate a Safe-Harbor #18 patient identifier
 * (person name, MRN, or a date tied to the individual) and must not be sent to
 * Amplitude. They are still written to the BigQuery `frontend_logs` sink, which
 * has stricter access controls; only the external Amplitude event is scrubbed.
 *
 * These keys are removed here, at the single point where an event reaches
 * `amplitude.track`, rather than at every call site, so any event — current or
 * future — is covered without touching each logger.
 */
const AMPLITUDE_PHI_ADDITIONAL_DATA_KEYS = [
  "patient_name",
  "patient_mrn",
  "date_of_service",
  "date_of_work",
  "patient_id",
  "member_id",
] as const;

/**
 * `additional_data` keys that hold provider-typed free text and must not be
 * sent to Amplitude. Free text can embed patient identifiers (a search query
 * is commonly a name or MRN), and there is no Safe-Harbor carve-out for it, so
 * the whole value is dropped for Amplitude while BigQuery retains it.
 *
 * `new_value`/`old_value` also carry dropdown/checkbox/date *selections* on
 * some `form_field_edited`/`*_changed` events; those non-free-text values are
 * dropped from Amplitude as an accepted trade-off of key-based scrubbing.
 * `ai_reasoning_text`/`supporting_evidence_texts`/`contradictory_evidence_texts`
 * are model-generated but patient-tied, so they are scrubbed for the same
 * reason.
 */
const AMPLITUDE_FREE_TEXT_ADDITIONAL_DATA_KEYS = [
  "search_words",
  "search_query",
  "comments",
  "comment",
  "incorrect_reason_text",
  "human_verdict_reason",
  "ai_improvement_text",
  "skip_reason",
  "change_reason_text",
  "change_source_detail",
  "cpt_description",
  "doc_title",
  "new_value",
  "old_value",
  "ai_reasoning_text",
  "supporting_evidence_texts",
  "contradictory_evidence_texts",
  "document_name",
] as const;

const AMPLITUDE_SCRUBBED_ADDITIONAL_DATA_KEYS = [
  ...AMPLITUDE_PHI_ADDITIONAL_DATA_KEYS,
  ...AMPLITUDE_FREE_TEXT_ADDITIONAL_DATA_KEYS,
];

/**
 * `field_key` values (from the manual EV coverage edit event) whose
 * `value_entered` holds a Safe-Harbor #18 identifier — a health-plan
 * beneficiary/member number or a date tied to the individual — and so must be
 * suppressed from Amplitude. The BV financial keys (deductible, copay, etc.)
 * are not Safe-Harbor identifiers and are left intact.
 */
const PHI_FIELD_KEYS_FOR_VALUE_ENTERED = new Set([
  "member_id",
  "effective_date",
  "plan_end_date",
]);

/**
 * Returns a shallow copy of `data` with the PHI identifier keys and free-text
 * keys removed. Also suppresses `value_entered` when its `field_key` names a
 * Safe-Harbor #18 identifier (member id / person-tied date), since that value
 * would otherwise leak a patient identifier to Amplitude.
 *
 * Returns `data` unchanged (same reference) when nothing scrubbed is present,
 * to avoid needlessly allocating a new object on the common no-PHI path.
 */
const stripPhidentifiersFromAdditionalData = (
  data: Record<string, unknown>,
): Record<string, unknown> => {
  const suppressValueEntered = PHI_FIELD_KEYS_FOR_VALUE_ENTERED.has(
    String(data.field_key ?? ""),
  );
  if (
    !AMPLITUDE_SCRUBBED_ADDITIONAL_DATA_KEYS.some((key) => key in data) &&
    !suppressValueEntered
  ) {
    return data;
  }
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const isScrubbedKey = (
      AMPLITUDE_SCRUBBED_ADDITIONAL_DATA_KEYS as readonly string[]
    ).includes(key);
    const isSensitiveValueEntered =
      key === "value_entered" && suppressValueEntered;
    if (!isScrubbedKey && !isSensitiveValueEntered) {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
};

/**
 * Sends an event to Amplitude with the standard base envelope plus caller-
 * supplied additional data merged on top (with patient identifiers and free
 * text scrubbed), and console-logs the payload tagged with success or failure
 * status. Never throws; analytics must not block UX.
 */
export const logEventToAmplitude = (
  baseInput: BaseEventInput,
  additionalData: Record<string, unknown> = {},
): void => {
  const payload: AmplitudeEventPayload = {
    ...buildBaseEvent(baseInput),
    ...stripPhidentifiersFromAdditionalData(additionalData),
  };

  try {
    amplitude.track(baseInput.event_name, payload);
    logAmplitudeEvent(baseInput.event_name, payload, "success");
  } catch (error) {
    logAmplitudeEvent(baseInput.event_name, payload, "failure", error);
  }
};
